import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  createPricingPlan,
  listPricingPlans,
  type BillingCycle,
  type CreatePricingPlanInput,
  type PricingPlan,
} from "../api/pricingPlans";
import { formatCurrencyAmount } from "./BillingStatsView";

const CURRENCIES = ["PYG", "USD"];

const BILLING_CYCLE_OPTIONS: { value: BillingCycle; label: string }[] = [
  { value: "monthly", label: "Mensual" },
  { value: "quarterly", label: "Trimestral (mar/jun/sep/dic)" },
  { value: "yearly", label: "Anual (diciembre)" },
];

const BILLING_CYCLE_LABEL: Record<BillingCycle, string> = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  yearly: "Anual",
};

const EMPTY_FORM: CreatePricingPlanInput = {
  name: "",
  monthlyFee: 0,
  monthlyFeeCurrency: "PYG",
  txFeeFixed: 0,
  txFeePercentage: 0,
  txFeeCurrency: "PYG",
  includedTxPerMonth: undefined,
  overQuotaTxFee: undefined,
  billingCycle: "monthly",
  free: false,
  selfBillingEnabled: false,
};

export function PricingPlanSection({ merchantId }: { merchantId: string }) {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    listPricingPlans(merchantId)
      .then(setPlans)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Error cargando planes"),
      )
      .finally(() => setLoading(false));
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activePlan = plans.find((p) => p.active) ?? null;
  const history = plans.filter((p) => !p.active);

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          Plan vigente
        </Typography>
        <Button variant="contained" size="small" onClick={() => setDialogOpen(true)}>
          {activePlan ? "Cambiar plan" : "Asignar plan"}
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !activePlan && (
        <Alert severity="info">
          Este comercio no tiene un plan asignado todavía. Sin plan, no se generan
          facturas — el comercio sigue operando pero Novasis no le cobra fee.
        </Alert>
      )}

      {activePlan && <ActivePlanCard plan={activePlan} />}

      {history.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Historial
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {history.map((p) => (
              <HistoricalPlanRow key={p.id} plan={p} />
            ))}
          </Stack>
        </Box>
      )}

      <NewPlanDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        merchantId={merchantId}
        onCreated={() => {
          setDialogOpen(false);
          refresh();
        }}
      />
    </Stack>
  );
}

function ActivePlanCard({ plan }: { plan: PricingPlan }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {plan.name}
        </Typography>
        {plan.free && <Chip label="FREE" color="info" size="small" />}
        {plan.selfBillingEnabled && (
          <Chip label="Self-billing" color="secondary" size="small" />
        )}
        <Chip label="Vigente" color="success" size="small" />
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack spacing={1.5}>
        <Row label="Ciclo de cobro" value={BILLING_CYCLE_LABEL[plan.billingCycle]} />
        <Row
          label="Fee mensual"
          value={`${formatCurrencyAmount(plan.monthlyFee, plan.monthlyFeeCurrency)} ${plan.monthlyFeeCurrency}`}
        />
        <Row
          label="Fee fijo por tx aprobada"
          value={`${formatCurrencyAmount(plan.txFeeFixed, plan.txFeeCurrency)} ${plan.txFeeCurrency}`}
        />
        <Row
          label="Fee porcentual"
          value={`${Number(plan.txFeePercentage)}%`}
        />
        {plan.includedTxPerMonth !== null && (
          <Row label="Tx incluidas / mes" value={String(plan.includedTxPerMonth)} />
        )}
        {plan.overQuotaTxFee !== null && (
          <Row
            label="Tx sobre cuota"
            value={`${formatCurrencyAmount(plan.overQuotaTxFee, plan.monthlyFeeCurrency)} ${plan.monthlyFeeCurrency}`}
          />
        )}
        <Row
          label="Vigente desde"
          value={new Date(plan.validFrom).toLocaleString()}
        />
      </Stack>
    </Paper>
  );
}

function HistoricalPlanRow({ plan }: { plan: PricingPlan }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2 }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {plan.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(plan.validFrom).toLocaleDateString()} —{" "}
          {plan.validUntil ? new Date(plan.validUntil).toLocaleDateString() : "?"}
        </Typography>
      </Box>
      <Typography variant="caption" fontFamily="monospace">
        {formatCurrencyAmount(plan.monthlyFee, plan.monthlyFeeCurrency)}{" "}
        {plan.monthlyFeeCurrency} / mes
      </Typography>
    </Paper>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 200 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily="monospace">
        {value}
      </Typography>
    </Stack>
  );
}

function NewPlanDialog({
  open,
  onClose,
  merchantId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  merchantId: string;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreatePricingPlanInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError(null);
    }
  }, [open]);

  const update = <K extends keyof CreatePricingPlanInput>(
    key: K,
    value: CreatePricingPlanInput[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await createPricingPlan(merchantId, {
        ...form,
        monthlyFee: Number(form.monthlyFee) || 0,
        txFeeFixed: Number(form.txFeeFixed) || 0,
        txFeePercentage: Number(form.txFeePercentage) || 0,
        includedTxPerMonth:
          form.includedTxPerMonth === undefined ||
          form.includedTxPerMonth === null
            ? undefined
            : Number(form.includedTxPerMonth),
        overQuotaTxFee:
          form.overQuotaTxFee === undefined || form.overQuotaTxFee === null
            ? undefined
            : Number(form.overQuotaTxFee),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error guardando plan");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = form.name.trim().length >= 2 && !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo plan de pricing</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Al guardar, el plan vigente actual (si existe) se expirará automáticamente
            y este pasa a ser el activo.
          </Alert>

          <TextField
            label="Nombre del plan"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            fullWidth
            autoFocus
            placeholder="Plan Estándar PY 2026"
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Fee mensual"
              type="number"
              value={form.monthlyFee}
              onChange={(e) => update("monthlyFee", Number(e.target.value))}
              fullWidth
            />
            <TextField
              select
              label="Moneda mensual"
              value={form.monthlyFeeCurrency ?? "PYG"}
              onChange={(e) => update("monthlyFeeCurrency", e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Fee fijo por tx"
              type="number"
              value={form.txFeeFixed}
              onChange={(e) => update("txFeeFixed", Number(e.target.value))}
              fullWidth
              helperText="Cobrado por cada tx aprobada"
            />
            <TextField
              select
              label="Moneda tx"
              value={form.txFeeCurrency ?? "PYG"}
              onChange={(e) => update("txFeeCurrency", e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Fee porcentual (%)"
            type="number"
            value={form.txFeePercentage}
            onChange={(e) => update("txFeePercentage", Number(e.target.value))}
            inputProps={{ step: 0.1 }}
            helperText="Sobre el monto aprobado. 0.3 = 0,3%"
          />

          <Divider />
          <TextField
            select
            label="Ciclo de cobro"
            value={form.billingCycle ?? "monthly"}
            onChange={(e) =>
              update("billingCycle", e.target.value as BillingCycle)
            }
            helperText="Mensual: factura todos los meses. Trimestral: solo en mar/jun/sep/dic (×3 fee mensual). Anual: solo en diciembre (×12)."
          >
            {BILLING_CYCLE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.free ?? false}
                  onChange={(e) => update("free", e.target.checked)}
                />
              }
              label="Plan free (sin cobro)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.selfBillingEnabled ?? false}
                  onChange={(e) =>
                    update("selfBillingEnabled", e.target.checked)
                  }
                  disabled={form.free}
                />
              }
              label="Self-billing (cobrar vía Novasis Pay)"
            />
          </Stack>

          <Divider />
          <Typography variant="caption" color="text.secondary">
            Cuota incluida (opcional)
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Tx incluidas / mes"
              type="number"
              value={form.includedTxPerMonth ?? ""}
              onChange={(e) =>
                update(
                  "includedTxPerMonth",
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
              fullWidth
            />
            <TextField
              label="Tx sobre cuota"
              type="number"
              value={form.overQuotaTxFee ?? ""}
              onChange={(e) =>
                update(
                  "overQuotaTxFee",
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
              fullWidth
            />
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {saving ? "Guardando…" : "Guardar plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
